import cookies from 'js-cookie'
import moment from 'moment'
import { useCallback, useState } from 'react'

import { ERROR_GET_AUTH_CLIENT, ERROR_GET_AUTH_SERVER } from '@/app/module/auth/definitions'
import { setToken } from '@/app/module/auth/helpers'
import { getUserPreferences, setOtp } from '@/app/module/auth/store/session.slice'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser, setPageLoadSource } from '@/app/module/user/store/actions'
import { esApi, isErrorResponse } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { isLegacyApiError } from '@/app/types/error'

export type LoginValues = {
  username: string
  password: string
  verifyToken: string
}

export const useSignin = () => {
  const dispatch = useAppDispatch()
  const [authSignin, mutationResult] = esApi.endpoints.authSignin.useMutation()
  const [isLoading, setIsLoading] = useState(false)

  const login = useCallback(
    async (values: LoginValues) => {
      setIsLoading(true)

      try {
        const response = await authSignin({
          body: {
            username: values.username,
            password: values.password,
            verify_token: values.verifyToken,
          },
        }).unwrap()

        const { access: authToken, totpEnabled, validUntil = '' } = response

        if (totpEnabled) {
          const expiryDate = validUntil ? moment(validUntil).toDate() : moment().add(7, 'day').toDate()
          if (authToken) {
            cookies.set('otpToken', authToken, { expires: expiryDate, sameSite: 'strict', secure: true })
            dispatch(setOtp(authToken))
          }
          return { authToken, totpEnabled }
        }

        if (authToken) {
          setToken(authToken, validUntil)
        }

        setPageLoadSource('login')

        const user = await dispatch(getUser({ token: authToken }))

        if (user && !(user instanceof Error)) {
          dispatch(getUserPreferences({ user, token: authToken }))
        }

        return { user, authToken, totpEnabled: false }
      } catch (error) {
        const errorMessage = getSigninErrorMessage(error)
        dispatch(createNotification({ 'login-error': { type: 'error', message: errorMessage } }))
        return false
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, authSignin],
  )

  return [login, { ...mutationResult, isLoading }] as const
}

export const getSigninErrorMessage = (error: unknown): string => {
  const isRTKError = isErrorResponse(error)
  const isESApiError = isLegacyApiError(error)

  let data: { error: string; errorCode?: string } | undefined
  let status: number | undefined

  if (isRTKError) {
    data = error.data
    status = error.status
  } else if (isESApiError) {
    data = error.response
    status = error.status
  }

  if (status && status >= 400 && status < 500) {
    if (data?.errorCode === 'login.locked' && data?.error) {
      return data.error
    }
    return ERROR_GET_AUTH_CLIENT
  }
  return ERROR_GET_AUTH_SERVER
}
